import React, {
    useState,
    useEffect,
} from 'react'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import {
    readCompanies,
    readUsers,
    User,
    Company,
} from './helpers'
import { withConnectWrapper } from '../../../validator'
import type { RootState } from '../../../store'
import type { AuthedUser } from '../../../types/domain'
import Modal from '../../../components/shared/modal'
import UserForm from './forms/user-form'
import TableCard from './TableCard'
import UsersTable from './UsersTable'

type AnyProps = Record<string, unknown>

interface SuperAdminUsersIndexProps {
    authedUser: { uid?: string; superAdmin?: boolean } | null
    users: User[]
    companies: Company[]
}

/**
 * Super-admin users management page
 */
function SuperAdminUsersIndex(_props: SuperAdminUsersIndexProps) {
    const { t } = useTranslation()

    const [addUserModal, setAddUserModal] = useState(false)
    const [updatedUser, setUpdatedUser] = useState<User | null>(null)

    /**
     * On mount load data
     */
    useEffect(() => {
        Promise
            .all([
                readUsers(),
                readCompanies(),
            ])
    }, [])

    /**
     * Show/hide modal and set/unset user
     *
     * @param {boolean} showModal
     * @param {User|null} user
     */
    const toggleModal = (showModal = false, user: User | null = null): void => {
        setAddUserModal(showModal)
        setUpdatedUser(user)
    }

    return (
        <div className="container">
            <TableCard
                toggleModal={() => toggleModal(true)}
                title={t('users.addUser')}
                buttonText={t('users.addUser')}
            >
                <UsersTable toggleModal={toggleModal}/>
            </TableCard>

            {addUserModal && (
                <Modal
                    description={!updatedUser ? t('users.form.register') : t('users.form.edit', { userName: `${updatedUser.name} ${updatedUser.surname}` })}
                    handleHideModal={() => toggleModal()}
                    content={() => (
                        <UserForm
                            updatedUser={updatedUser}
                            successAction={() => toggleModal()}
                        />
                    )}
                />
            )}
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    authedUser: state.auth.user as AuthedUser | null,
    users: state.superAdmin.users as User[],
    companies: state.superAdmin.companies as Company[],
})

export default withConnectWrapper(mapStateToProps)(SuperAdminUsersIndex as unknown as ComponentType<AnyProps>)
